import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useAdminTheme } from '../hooks/useAdminTheme';
import { Helmet } from 'react-helmet-async';
import { FaRss } from 'react-icons/fa';
import { EmptyState } from '../components/admin/ui/AdminUI';
import '../styles/admin-theme.css';
import AuthGuard from '../components/AuthGuard';
import AdminNavigationModern from '../components/AdminNavigationModern';
import AdminDashboard from '../components/admin/AdminDashboard';
import EmailStatusChecker from '../components/admin/EmailStatusChecker';
import VehicleManager from '../components/admin/VehicleManager';
import DealManagement from '../components/admin/DealManagement';
import ServiceAppointmentManager from '../components/admin/ServiceAppointmentManager';
import LeadManagement from '../components/admin/LeadManagement';
import PaymentManagement from '../components/admin/PaymentManagement';
import AdminSettings from '../components/admin/AdminSettings';
import AdminAnalytics from '../components/admin/AdminAnalytics';
import AIChatAdmin from '../components/admin/AIChatAdmin';
import SMSManager from '../components/admin/SMSManager';
import TwoFactorSetup from '../components/admin/TwoFactorSetup';
import AuthorizeNetTest from '../components/AuthorizeNetTest';
import ContractDashboard from '../components/admin/ContractDashboard';
import FormsManager from '../components/admin/FormsManager';
import DealBuilder from '../components/admin/DealBuilder';
import CreditApplications from '../components/admin/CreditApplications';
import CreditBureauSettings from '../components/admin/CreditBureauSettings';
import SubscriptionManager from '../components/admin/SubscriptionManager';
import CreditCheckManager from '../components/admin/CreditCheckManager';
import CreditConsentDashboard from '../components/admin/CreditConsentDashboard';
import PartsStoreManager from '../components/admin/PartsStoreManager';
import QuickDealCalculator from '../components/admin/QuickDealCalculator';
import BHPHAccounts from '../components/admin/BHPHAccounts';
import ReportsHub from '../components/admin/ReportsHub';
import VINDecoder from './VINDecoder';
// import { auditService } from '../services/auditService'; // Temporarily disabled

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [creditPullTarget, setCreditPullTarget] = useState(null);
  const { user } = useAuth();
  const { theme, cycleTheme, nextTheme } = useAdminTheme();

  // Session timeout with 30 minute timeout, 5 minute warning
  useSessionTimeout(30, 5);
  
  // Log admin access
  useEffect(() => {
    if (user) {
      // auditService.logAdminAction('admin_panel_access', {
      //   email: user.email,
      //   page: 'admin_dashboard'
      // });
    }
  }, [user]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard setActiveTab={setActiveTab} />;
      case 'vehicles':
        return <VehicleManager unitType="vehicle" />;
      case 'golf-carts':
        return <VehicleManager unitType="golf-cart" />;
      case 'trailers':
        return <VehicleManager unitType="trailer" />;
      case 'boats':
        return <VehicleManager unitType="boat" />;
      case 'deals':
        return <DealManagement />;
      case 'deal-builder':
        return <DealBuilder />;
      case 'quick-deal':
        return <QuickDealCalculator />;
      case 'bhph':
        return <BHPHAccounts />;
      case 'reports':
        return <ReportsHub />;
      case 'forms':
        return <FormsManager />;
      case 'credit-apps':
        return <CreditApplications onRunCreditPull={(app) => { setCreditPullTarget(app); setActiveTab('credit-check'); }} />;
      case 'credit-settings':
        return <CreditBureauSettings />;
      case 'credit-check':
        return <CreditCheckManager preloadApplication={creditPullTarget} onPreloadConsumed={() => setCreditPullTarget(null)} />;
      case 'credit-consent':
        return <CreditConsentDashboard />;
      case 'contracts':
        return <ContractDashboard />;
      case 'service':
        return <ServiceAppointmentManager />;
      case 'leads':
        return <LeadManagement />;
      case 'payments':
        return <PaymentManagement />;
      case 'subscriptions':
        return <SubscriptionManager />;
      case 'payment-test':
        return <AuthorizeNetTest />;
      case 'sms':
        return <SMSManager />;
      case 'security':
        return <TwoFactorSetup />;
      case 'ai-chat':
        return <AIChatAdmin />;
      case 'analytics':
        return <AdminAnalytics />;
      case 'vin-decoder':
        return <VINDecoder />;
      case 'parts-store':
        return <PartsStoreManager />;
      case 'google-vla':
        return (
          <EmptyState
            icon={FaRss}
            title="Google VLA Feed"
            description="Feed is manually managed via Google Merchant Center. No generator needed."
          />
        );
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard setActiveTab={setActiveTab} />;
    }
  };

  return (
    <AuthGuard>
      {/* `pp-admin` scopes the entire admin design-token set. Nothing outside
          this wrapper is affected by the admin theme. */}
      <div className="pp-admin min-h-screen" data-theme={theme}>
        <Helmet>
          <title>Admin Dashboard - Paradise-Performance</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <AdminNavigationModern
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          theme={theme}
          nextTheme={nextTheme}
          onCycleTheme={cycleTheme}
        >
          <main className="min-w-0 p-4 sm:p-6 max-w-[1600px]">
            {/* Security Alert for 2FA */}
            {user && (
              <div className="mb-4 flex items-center gap-2 rounded-adm-ctl bg-adm-blue-tint px-3 py-2">
                <p className="text-[13px] text-adm-text-mid">
                  <span className="text-adm-text-hi font-medium">Security notice:</span>{' '}
                  Two-factor authentication is available.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('security')}
                  className="text-[13px] font-medium text-adm-blue-text hover:underline"
                >
                  Set up 2FA
                </button>
              </div>
            )}
            {/* Email configuration warning */}
            <EmailStatusChecker />

            {/* 160ms ease-out fade + 4px rise on tab change */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
            >
              {renderContent()}
            </motion.div>
          </main>
        </AdminNavigationModern>
      </div>
    </AuthGuard>
  );
};

export default Admin;